import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JobRunDetail from "./JobRunDetail";
import JobRunsChart from "./JobRunsChart";
import JobRunsMinimap from "./JobRunsMinimap";
import {
  WINDOW_SIZE,
  clampWindow,
  formatDateRange,
  isAtLatest,
  isAtOldestLoaded,
  latestWindowStart,
  mergeRuns,
} from "./history";
import { ScheduledJobRunsAggregate, buildKpiCells } from "./summary";
import { ScheduledJobRun, UnmatchedUrl } from "./types";
import { useBackdropDismiss } from "../../hooks/useBackdropDismiss";

// bd-deepcontext-hfv7u.6 — windowed run history + minimap + cursor lazy-load.
//
// The modal opens on the latest window of runs and pages through history with
// Older / Newer / Latest. A minimap below the header spans the whole fetched
// history and shows (and moves) the current window. Older pages lazy-load from
// the cursor endpoint (`{ runs, nextCursor }`) as you page back; the live SSE
// run set (the `runs` prop) is merged on top so in-flight runs stay current.
//
// The chart (.4) and per-run detail (.5) are consumed unchanged — they render
// the windowed slice. Default selection is the latest run.
export interface JobRunsModalProps {
  jobName: string;
  jobId: string;
  runs: ScheduledJobRun[];
  isLive: boolean;
  onClose: () => void;
  // bd-hfv7u.13 — the job's per-run URL target ("requested"), threaded into the
  // chart so it can draw the amber fulfillment line (urlsProcessed / urlsPerRun).
  // Optional/legacy-safe: when missing/0 the chart omits the fulfillment line.
  urlsPerRun?: number;
  // bd-hfv7u.6 — lazy-loader for older history pages. Calls the cursor endpoint
  // (`?cursor`); returns one latest-first page + the next cursor (null at end).
  fetchRunsPage: (
    cursor?: string,
  ) => Promise<{ runs: ScheduledJobRun[]; nextCursor: string | null }>;
  // bd-hfv7u.7 — per-job aggregate loader. Calls the aggregate endpoint so the
  // KPI strip is correct over the WHOLE run history, not just the fetched
  // window. Returns counts + avg match-rate/duration computed server-side.
  fetchAggregate: () => Promise<ScheduledJobRunsAggregate>;
  // bd-hfv7u.7 — schedule meta for the header line. All optional / legacy: the
  // modal guards each before rendering so older jobs without these render only
  // the fields they have.
  startDate?: string;
  endDate?: string;
  lastRunAt?: string;
  nextRunAt?: string;
  // bd-hfv7u.11 — shared relative-time helpers from ScheduledJobs so the compact
  // schedule line ("Active Jun 24 → Jul 31 · ran 12m ago · next in 48m") matches
  // the row's formatting. `shortDate` → "Jun 24"; `rel` → "12m" (caller adds
  // " ago"); `relFuture` → "in 48m".
  shortDate: (dateString: string) => string;
  rel: (dateString: string) => string;
  relFuture: (dateString: string) => string;
  // Unmatched-URL drill-down (per run) state, owned by ScheduledJobs.
  expandedRunId: string | null;
  unmatchedSort: "lowest" | "highest";
  unmatchedUrls: UnmatchedUrl[];
  unmatchedLoading: boolean;
  unmatchedError: string | null;
  // Handlers wired to the API in ScheduledJobs.
  onCancelRun: (jobId: string, runId: string) => void;
  onToggleDrilldown: (jobId: string, runId: string) => void;
  onChangeUnmatchedSort: (
    jobId: string,
    runId: string,
    sort: "lowest" | "highest",
  ) => void;
  // Shared formatting helpers so output stays identical to the inline version.
  formatDate: (dateString: string, showTimezone?: boolean) => string;
  calculateDuration: (startedAt?: string, completedAt?: string) => string;
  getRunStatusColor: (status: string) => string;
  getRunStatusIcon: (status: string) => React.ReactNode;
}

const JobRunsModal: React.FC<JobRunsModalProps> = ({
  jobName,
  jobId,
  runs,
  isLive,
  onClose,
  urlsPerRun,
  fetchRunsPage,
  fetchAggregate,
  startDate,
  endDate,
  lastRunAt,
  nextRunAt,
  shortDate,
  rel,
  relFuture,
  expandedRunId,
  unmatchedSort,
  unmatchedUrls,
  unmatchedLoading,
  unmatchedError,
  onCancelRun,
  onToggleDrilldown,
  onChangeUnmatchedSort,
  formatDate,
  calculateDuration,
  getRunStatusColor,
  getRunStatusIcon,
}) => {
  // Older pages fetched via the cursor endpoint. Merged with the live `runs`
  // prop (which the parent feeds from SSE) into the chronological `history`.
  const [olderRuns, setOlderRuns] = useState<ScheduledJobRun[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Desired window start (chronological index). null = pinned to the latest
  // window — it re-pins to the newest run whenever history grows (SSE/load).
  const [windowStart, setWindowStart] = useState<number | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  // bd-hfv7u.7 — per-job aggregate for the KPI strip, sourced from the aggregate
  // endpoint (correct beyond the fetched window). null until loaded / on error.
  const [aggregate, setAggregate] = useState<ScheduledJobRunsAggregate | null>(
    null,
  );
  // Guards so the first-page fetch fires once per job.
  const initialisedJobRef = useRef<string | null>(null);
  // Guards the aggregate fetch so it fires once per job — without this, the
  // parent's inline `fetchAggregate` identity changes on every SSE re-render,
  // refiring the effect into a refetch loop + KPI flicker.
  const aggregateJobRef = useRef<string | null>(null);
  // bd-hfv7u.12 — the parent (ScheduledJobs) passes `fetchRunsPage` /
  // `fetchAggregate` as INLINE arrows, so their identity changes on every SSE
  // re-render. The job-init / aggregate effects must NOT depend on those
  // identities (depending on `fetchRunsPage` re-ran the init effect on every
  // SSE tick — wiping `aggregate` back to null so the KPI cards stuck on "—",
  // and resetting windowStart/olderRuns/nextCursor so Older paging never held).
  // Read the latest callbacks through refs and key both effects off `jobId`.
  const fetchRunsPageRef = useRef(fetchRunsPage);
  const fetchAggregateRef = useRef(fetchAggregate);
  fetchRunsPageRef.current = fetchRunsPage;
  fetchAggregateRef.current = fetchAggregate;

  // Full chronological history: live runs + fetched older pages, deduped.
  const history = useMemo(
    () => mergeRuns(runs, olderRuns),
    [runs, olderRuns],
  );
  const total = history.length;
  const latestRun = total > 0 ? history[total - 1] : null;

  // bd-hfv7u.9 — the single run shown in the detail panel, driven by chart
  // bar-click selection (default = latest run, set in the selection effect).
  const selectedRun = useMemo(
    () =>
      selectedRunId
        ? history.find((r) => r.id === selectedRunId) ?? null
        : null,
    [history, selectedRunId],
  );

  // Resolve the concrete window: pinned-to-latest when windowStart is null.
  const win = useMemo(() => {
    const desired =
      windowStart === null ? latestWindowStart(WINDOW_SIZE, total) : windowStart;
    return clampWindow(desired, WINDOW_SIZE, total);
  }, [windowStart, total]);

  const windowRuns = useMemo(
    () => history.slice(win.start, win.end),
    [history, win.start, win.end],
  );

  // Fetch the first cursor page once the modal opens for a job, so the minimap
  // and Older paging have the nextCursor even before the user pages back.
  //
  // bd-hfv7u.12 — the ref guard must be StrictMode-safe. The previous version
  // latched `initialisedJobRef = jobId` SYNCHRONOUSLY, before the async fetch
  // resolved. Under React 18 StrictMode the effect mounts, is torn down (its
  // cleanup sets `cancelled = true`, discarding the in-flight result), then
  // re-mounts — where the guard saw the ref already === jobId and bailed, so the
  // page never loaded. Fix: LATCH the guard only once the fetch resolves; on a
  // cancelled teardown, clear the guard so the remount refetches.
  useEffect(() => {
    if (initialisedJobRef.current === jobId) return;
    setOlderRuns([]);
    setNextCursor(null);
    setWindowStart(null);
    setSelectedRunId(null);
    setLoadError(null);
    // NOTE: the aggregate is owned by the aggregate effect below — do NOT reset
    // it here.
    let cancelled = false;
    let completed = false;
    (async () => {
      try {
        setLoadingOlder(true);
        const page = await fetchRunsPageRef.current(undefined);
        if (cancelled) return;
        completed = true;
        initialisedJobRef.current = jobId;
        setOlderRuns(page.runs);
        setNextCursor(page.nextCursor);
      } catch {
        if (!cancelled) setLoadError("Failed to load run history");
      } finally {
        if (!cancelled) setLoadingOlder(false);
      }
    })();
    return () => {
      cancelled = true;
      // Teardown before the fetch resolved (StrictMode dev remount, or the modal
      // closing mid-flight): don't leave the guard latched — let the next mount
      // re-run the fetch.
      if (!completed && initialisedJobRef.current !== jobId) {
        initialisedJobRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // bd-hfv7u.7 — load the per-job aggregate for the KPI strip whenever the job
  // changes. Independent of the cursor pages so the KPIs reflect the full
  // history, not the loaded window. On failure the strip falls back to dashes.
  //
  // bd-hfv7u.12 — ROOT CAUSE of defect #1 ("all four KPI cards stuck on —"): the
  // guard ref was latched synchronously, so StrictMode's mount→teardown→remount
  // cancelled the first fetch and the remount bailed on the guard — the 200
  // aggregate response was never applied. Same StrictMode-safe latching as the
  // page effect above: latch only on success, clear on a cancelled teardown.
  useEffect(() => {
    if (aggregateJobRef.current === jobId) return;
    let cancelled = false;
    let completed = false;
    setAggregate(null);
    (async () => {
      try {
        const agg = await fetchAggregateRef.current();
        if (cancelled) return;
        completed = true;
        aggregateJobRef.current = jobId;
        setAggregate(agg);
      } catch {
        if (!cancelled) setAggregate(null);
      }
    })();
    return () => {
      cancelled = true;
      if (!completed && aggregateJobRef.current !== jobId) {
        aggregateJobRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Default selection = latest run. Re-pin when the selection falls out of the
  // history (e.g. SSE replaced the set) or hasn't been chosen yet.
  useEffect(() => {
    if (total === 0) {
      if (selectedRunId !== null) setSelectedRunId(null);
      return;
    }
    if (!selectedRunId || !history.some((r) => r.id === selectedRunId)) {
      setSelectedRunId(latestRun?.id ?? null);
    }
  }, [history, total, selectedRunId, latestRun]);

  // Fetch the next (older) page and prepend it. Returns the count of genuinely
  // new runs added at the oldest (chronological-front) end, so the caller can
  // keep the viewport visually stable after indices shift.
  const loadOlder = useCallback(async (): Promise<number> => {
    if (loadingOlder || nextCursor === null) return 0;
    try {
      setLoadingOlder(true);
      setLoadError(null);
      const page = await fetchRunsPageRef.current(nextCursor);
      const known = new Set(olderRuns.map((r) => r.id));
      const added = page.runs.filter((r) => !known.has(r.id)).length;
      setOlderRuns((prev) => mergeRuns([], [...prev, ...page.runs]));
      setNextCursor(page.nextCursor);
      return added;
    } catch {
      setLoadError("Failed to load older runs");
      return 0;
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, nextCursor, olderRuns]);

  // Move the window by a signed page (in WINDOW_SIZE steps). Paging older past
  // the oldest loaded run triggers a lazy fetch first, then shifts onto it.
  const moveWindow = useCallback(
    async (deltaWindows: number) => {
      const current = win.start;
      if (deltaWindows < 0 && isAtOldestLoaded(win) && nextCursor !== null) {
        const added = await loadOlder();
        // After prepending older runs, indices shift right by `added`; keep the
        // viewport visually stable, then step one window older.
        setWindowStart(Math.max(0, current + added - WINDOW_SIZE));
        return;
      }
      setWindowStart(current + deltaWindows * WINDOW_SIZE);
    },
    [win, nextCursor, loadOlder],
  );

  const goLatest = useCallback(() => setWindowStart(null), []);

  // Minimap drag/click sets an absolute window start; if it lands at the oldest
  // loaded edge and more history exists, pull the next page in.
  const onMinimapStart = useCallback(
    (start: number) => {
      if (start <= 0 && nextCursor !== null && !loadingOlder) {
        void loadOlder();
      }
      setWindowStart(start);
    },
    [nextCursor, loadingOlder, loadOlder],
  );

  const atLatest = isAtLatest(win, total);
  const canGoOlder =
    total > 0 && (!isAtOldestLoaded(win) || nextCursor !== null);

  // bd-hfv7u.10 — range/date header for the chart: "Jun 24 – Jun 30 · runs
  // 49–63 of N". N is the true total from the aggregate endpoint (falls back to
  // the fetched count until the aggregate loads). Absolute run positions are
  // derived from the newest end so they read 1..N oldest→newest: the newest
  // fetched run is position `grandTotal`, so a fetched index `i` is at
  // `grandTotal - total + 1 + i`.
  const grandTotal = aggregate ? aggregate.totalRuns : total;
  const offset = Math.max(0, grandTotal - total);
  const rangeDates = useMemo(() => formatDateRange(windowRuns), [windowRuns]);
  const fullRangeDates = useMemo(() => formatDateRange(history), [history]);
  const firstPos = total === 0 ? 0 : offset + win.start + 1;
  const lastPos = total === 0 ? 0 : offset + win.end;

  // bd-hfv7u.7 — KPI cells from the aggregate (em dashes until loaded).
  const kpiCells = useMemo(() => buildKpiCells(aggregate), [aggregate]);

  // bd-hfv7u.11 — compact one-line schedule meta under the title, matching the
  // mockup: "Active Jun 24 → Jul 31 · ran 12m ago · next in 48m". Each segment
  // is guarded so legacy jobs missing a field omit it (and the leading "·").
  const scheduleSegments = useMemo(() => {
    const segments: string[] = [];
    if (startDate && endDate) {
      segments.push(`Active ${shortDate(startDate)} → ${shortDate(endDate)}`);
    } else if (startDate) {
      segments.push(`Active from ${shortDate(startDate)}`);
    }
    if (lastRunAt) segments.push(`ran ${rel(lastRunAt)} ago`);
    if (nextRunAt) segments.push(`next ${relFuture(nextRunAt)}`);
    return segments;
  }, [startDate, endDate, lastRunAt, nextRunAt, shortDate, rel, relFuture]);

  const backdropDismiss = useBackdropDismiss(onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      {...backdropDismiss}
    >
      <div className="bg-surface rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-ink">Job Runs: {jobName}</h3>
            {/* bd-hfv7u.11 — Live Updates + compact schedule meta on one line
                directly under the title (per the agreed mockup). */}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
              {isLive && (
                <span className="flex items-center font-medium text-accent">
                  <ArrowPathIcon className="h-4 w-4 animate-spin mr-1" />
                  Live Updates
                </span>
              )}
              {scheduleSegments.map((segment, i) => (
                <React.Fragment key={segment}>
                  {(i > 0 || isLive) && (
                    <span aria-hidden className="text-ink-muted">
                      ·
                    </span>
                  )}
                  <span>{segment}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        {/* bd-hfv7u.7 — aggregate-sourced KPI strip (5 cards per the agreed
            mockup). Rendered for every job (even empty history); values come
            from the aggregate endpoint, not a reduce over the loaded page. */}
        <div className="mb-4">
          <dl className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {kpiCells.map((cell) => (
              <div
                key={cell.label}
                className="rounded-md border border-line bg-ghost-bg px-3 py-2"
              >
                <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                  {cell.label}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold text-ink tabular-nums">
                  {cell.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {total > 0 && (
          <>
            {/* bd-hfv7u.10 — chart header: title + range/date label on the left,
                Older / Newer / Latest pager on the right (per agreed design). */}
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
              <div>
                <h4 className="text-sm font-medium text-ink">
                  Match rate by run
                </h4>
                <div className="mt-0.5 text-xs text-ink-muted tabular-nums">
                  {rangeDates && <span>{rangeDates} · </span>}
                  <span>
                    runs {firstPos}–{lastPos} of {grandTotal}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void moveWindow(-1)}
                  disabled={!canGoOlder || loadingOlder}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-line text-ink hover:bg-ghost-bg disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Older
                </button>
                <button
                  type="button"
                  onClick={() => void moveWindow(1)}
                  disabled={atLatest}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-line text-ink hover:bg-ghost-bg disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Newer
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={goLatest}
                  disabled={atLatest}
                  className="px-2.5 py-1.5 text-sm rounded-md border border-line text-ink hover:bg-ghost-bg disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Latest
                </button>
                {loadingOlder && (
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-ink-muted" />
                )}
              </div>
            </div>

            {/* Legend (chart header is suppressed; rendered here so it sits
                under the range/pager row, matching the mockup).
                bd-deepcontext-od1h — Tailwind arbitrary-value classes, not
                inline `style`: the class strings must stay literal so
                Tailwind's content scanner can see them. Keep each one in sync
                with CHART_COLORS (JobRunsChart.tsx) and the matching mark. */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted mb-2">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--safe-fg)]" />
                Matched
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--chart-risk-soft)]" />
                Unmatched
              </span>
              {/* bd-deepcontext-qx0pm — dropped/not-classified band chip. */}
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--warn-fg)]" />
                Dropped (not classified)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--ink)]" />
                Match-rate trend
              </span>
              {/* bd-hfv7u.13 — dashed fulfillment line, only when a target is
                  available. */}
              {!!urlsPerRun && urlsPerRun > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-4 border-t-2 border-dashed border-[var(--warn-fg)]" />
                  Fulfillment rate
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--risk-fg)]" />
                Failed
              </span>
            </div>

            {loadError && (
              <div className="mb-3 text-sm text-risk">{loadError}</div>
            )}

            {/* Chart over the visible window only (header suppressed — the
                title / range / pager / legend live in the modal above). */}
            {/* bd-deepcontext-030xr — fixed-height chart card so the plot fills
                its section top-to-bottom (no white gap below the bars). The
                chart's SVG renders at height 100% and stretches to this box. */}
            <div className="rounded-xl border border-line bg-surface p-3 h-72">
              <JobRunsChart
                runs={windowRuns}
                selectedRunId={selectedRunId}
                onSelectRun={setSelectedRunId}
                showHeader={false}
                urlsPerRun={urlsPerRun}
              />
            </div>

            {/* Minimap over the full fetched history, BELOW the chart; click /
                drag to jump the window (per agreed design). */}
            {total > 1 && (
              <div className="mt-3">
                <JobRunsMinimap
                  runs={history}
                  window={win}
                  onWindowStartChange={onMinimapStart}
                  rangeLabel={fullRangeDates}
                />
              </div>
            )}
          </>
        )}

        {/* bd-hfv7u.9 — a single detail panel for the selected run (chart
            bar-click drives the selection; default = latest run), not a list. */}
        <div>
          {total === 0 ? (
            <div className="text-center text-ink-muted py-8">
              {loadingOlder ? "Loading runs…" : "No runs found for this job"}
            </div>
          ) : (
            selectedRun && (
              <JobRunDetail
                key={selectedRun.id}
                run={selectedRun}
                jobId={jobId}
                expandedRunId={expandedRunId}
                unmatchedSort={unmatchedSort}
                unmatchedUrls={unmatchedUrls}
                unmatchedLoading={unmatchedLoading}
                unmatchedError={unmatchedError}
                onCancelRun={onCancelRun}
                onToggleDrilldown={onToggleDrilldown}
                onChangeUnmatchedSort={onChangeUnmatchedSort}
                formatDate={formatDate}
                calculateDuration={calculateDuration}
                getRunStatusColor={getRunStatusColor}
                getRunStatusIcon={getRunStatusIcon}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default JobRunsModal;
